import { createMap, groupBy, selectMany } from "../omnia"
import { TerramapApi } from "./api"
import { getFieldKey, getFieldProvider } from "./fields"
import { applyDateFilter, Filters } from "./filters"
import { groupLayers } from "./layers"
import { Field, ImportTask, Layer, LayerType, Selection, SoilOptixSet } from "./types"

interface UpdateSelectionContext {

    layers: Layer[]
    sets: SoilOptixSet[]
    fields: Field[]
    layerTypes: LayerType[]

}

/**
 * Returns an updated "Selection" object after applying new filters
 *
 * Selections are built by applying filters to available layers
 * The remaining layers are then grouped by Terramap parameters (year, date, PK analysis type), then by field
 *
 * Note: the function was initially planned to be able to query the API based on the filters
 * This has not been implemented this way - for now, we load all the avaialble layers upfront,
 * so that the API object is not currentl required
 *
 * Depending on growth, it seems possible to this could change in the future, so I advise keeping
 * this function async, and keep on passing the API object, just in case
 */
export async function updateSelection(api: TerramapApi, context: UpdateSelectionContext, filters: Filters) {

    const { sets } = context
    const { date = undefined, setId = 0 } = filters

    // Find the selected set

    const set = setId
        ? sets.find(s => s.setId === setId)
        : undefined

    // Filter layers

    let { layers } = context

    if (set) {
        layers = layers.filter(l => set.fields?.some(id => l.fieldId === id))
    }

    if (date) {
        layers = applyDateFilter(layers, date)
    }

    const selection = setupSelection(context, filters, layers)

    return selection

}

export function setupSelection(context: UpdateSelectionContext, filters: Filters, layers: Layer[]): Selection {

    const fieldSetsProvider = getFieldSetsProvider(context.sets)

    const fieldsProvider = (groupKey: string, layers: Layer[]) => getFields(groupKey, layers, context.fields, fieldSetsProvider)

    const groups = groupLayers(layers).map(g => ({
        ...g,
        fields: fieldsProvider(g.key, g.layers)
    }))

    const fields = fieldsProvider("*", layers)

    const layerTypes = getLayerTypes(layers, context.layerTypes)

    return {
        filters,
        layers,
        groups,
        fields,
        layerTypes
    }

}

export function getSetsFromImports(tasks: ImportTask[]): SoilOptixSet[] {

    const allSets = selectMany(tasks.map(t => t.soilOptixSets)).filter(s => Boolean(s))

    const groups = groupBy(allSets, s => s.setId)

    const distinctSets = groups.map(g => ({
        setId: g.key,
        fields: selectMany(g.values.map(v => v.fields)).unique(id => id)
    }))

    return distinctSets

}

function getLayerTypes(layers: Layer[], types: LayerType[]) {

    const map = createMap(types, t => t.typeId)

    const usedTypes = layers
        .map(l => l.typeId)
        .unique(id => id)
        .map(id => map.get(id))
        .filter(t => Boolean(t))
        .sort((a, b) => a.typeId - b.typeId)

    return usedTypes

}

type FieldSetsProvider = ((fieldId: number) => number[])

/**
 * Return a function mapping a fieldId to the setIds linked to this field
 * @param sets All sets
 */
function getFieldSetsProvider(sets: SoilOptixSet[]): FieldSetsProvider {

    // Flat list of { fieldId, setId }
    const flat = selectMany(sets.map(s => s.fields.map(f => ({ fieldId: f, setId: s.setId }))))

    // Group by field
    const groups = groupBy(flat, f => f.fieldId)

    // Transform to fields with distinct setIds
    const fields = groups.map(g => ({
        fieldId: g.key,
        sets: g.values.map(v => v.setId).unique(id => id)
    }))

    // Map by fieldId
    const fieldsMap = createMap(fields, f => f.fieldId)

    // Provider
    return (fieldId: number) => fieldsMap.get(fieldId)?.sets

}

function getFields(groupKey: string, layers: Layer[], fields: Field[], fieldSetsProvider: FieldSetsProvider): Selection["fields"] {

    const fieldProvider = getFieldProvider(fields)

    const groups = groupBy(layers, l => l.fieldId)
        .map(g => ({
            key: getFieldKey(groupKey, g.key),
            fieldId: g.key,
            field: fieldProvider(g.key),
            setIds: fieldSetsProvider(g.key) ?? [],
            layers: g.values,
        }))

    const sortedGroups = omnia.numericTextSort(groups, t => t.field.name)

    return sortedGroups

}
